"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.processContactInfoMutations = processContactInfoMutations;
var _rdflib = require("rdflib");
var _solidUi = require("solid-ui");
var _rdfMutationHelpers = require("../shared/rdfMutationHelpers");
var _idNodeFactory = require("../shared/idNodeFactory");
var _texts = require("../../texts");
var _debug = require("../../utils/debug");
function buildPhoneStatements(subject, doc, node, phone) {
  const normalizedValue = phone.value.startsWith('tel:') ? phone.value : `tel:${phone.value}`;
  const valueNode = (0, _rdflib.sym)(normalizedValue);
  const inserts = [(0, _rdflib.st)(subject, _solidUi.ns.vcard('hasTelephone'), node, doc), (0, _rdflib.st)(node, _solidUi.ns.vcard('value'), valueNode, doc)];
  if (phone.type) {
    inserts.push((0, _rdflib.st)(node, _solidUi.ns.rdf('type'), _solidUi.ns.vcard(phone.type), doc));
  }
  return inserts;
}
function buildEmailStatements(subject, doc, node, email) {
  const normalizedValue = email.value.startsWith('mailto:') ? email.value : `mailto:${email.value}`;
  const valueNode = (0, _rdflib.sym)(normalizedValue);
  const inserts = [(0, _rdflib.st)(subject, _solidUi.ns.vcard('hasEmail'), node, doc), (0, _rdflib.st)(node, _solidUi.ns.vcard('value'), valueNode, doc)];
  if (email.type) {
    inserts.push((0, _rdflib.st)(node, _solidUi.ns.rdf('type'), _solidUi.ns.vcard(email.type), doc));
  }
  return inserts;
}
function buildAddressStatements(subject, doc, node, address) {
  const inserts = [(0, _rdflib.st)(subject, _solidUi.ns.vcard('hasAddress'), node, doc)];
  if (address.type) inserts.push((0, _rdflib.st)(node, _solidUi.ns.rdf('type'), _solidUi.ns.vcard(address.type), doc));
  if (address.streetAddress) inserts.push((0, _rdflib.st)(node, _solidUi.ns.vcard('street-address'), (0, _rdflib.literal)(address.streetAddress), doc));
  if (address.locality) inserts.push((0, _rdflib.st)(node, _solidUi.ns.vcard('locality'), (0, _rdflib.literal)(address.locality), doc));
  if (address.region) inserts.push((0, _rdflib.st)(node, _solidUi.ns.vcard('region'), (0, _rdflib.literal)(address.region), doc));
  if (address.postalCode) inserts.push((0, _rdflib.st)(node, _solidUi.ns.vcard('postal-code'), (0, _rdflib.literal)(address.postalCode), doc));
  if (address.countryName) inserts.push((0, _rdflib.st)(node, _solidUi.ns.vcard('country-name'), (0, _rdflib.literal)(address.countryName), doc));
  return inserts;
}
async function mutatePhoneEntries(store, subject, phoneOps, documentTextCache) {
  const doc = subject.doc();
  const existingPhoneNodes = store.each(subject, _solidUi.ns.vcard('hasTelephone'), null, doc);
  const deletions = [];
  const insertions = [];
  phoneOps.remove.forEach(phone => {
    if (!phone.entryNode) return;
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingPhoneNodes, phone.entryNode);
    if (existingNode) {
      const linkedPhoneStatements = (0, _rdfMutationHelpers.collectLinkedNodeStatements)(store, subject, _solidUi.ns.vcard('hasTelephone'), doc);
      const matchingLinkStatement = linkedPhoneStatements.linkStatements.find(statement => statement.object?.value === existingNode.value);
      if (matchingLinkStatement) {
        deletions.push(matchingLinkStatement);
      }
      deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
    }
  });
  phoneOps.update.forEach(phone => {
    if (!phone.entryNode) return;
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingPhoneNodes, phone.entryNode);
    if (!existingNode) {
      insertions.push(...buildPhoneStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), phone));
      return;
    }
    const linkedPhoneStatements = (0, _rdfMutationHelpers.collectLinkedNodeStatements)(store, subject, _solidUi.ns.vcard('hasTelephone'), doc);
    const matchingLinkStatement = linkedPhoneStatements.linkStatements.find(statement => statement.object?.value === existingNode.value);
    if (matchingLinkStatement) {
      deletions.push(matchingLinkStatement);
    }
    deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
    insertions.push(...buildPhoneStatements(subject, doc, existingNode, phone));
  });
  phoneOps.create.forEach(phone => {
    insertions.push(...buildPhoneStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), phone));
  });
  const shouldSerializeDocument = await (0, _rdfMutationHelpers.shouldForceDocumentPutForStatements)(store, doc, insertions, undefined, {
    documentTextCache
  });
  await (0, _rdfMutationHelpers.runUpdateTransport)(store, doc, deletions, insertions, {
    unsupportedMessage: _texts.updaterUnsupportedStoreErrorMessageText,
    failureMessage: 'Failed to save contact info updates',
    documentTextCache,
    useDavFallback: false,
    usePutFallback: shouldSerializeDocument,
    forcePut: shouldSerializeDocument
  });
}
async function mutateEmailEntries(store, subject, emailOps, documentTextCache) {
  const doc = subject.doc();
  const existingEmailNodes = store.each(subject, _solidUi.ns.vcard('hasEmail'), null, doc);
  const deletions = [];
  const insertions = [];
  emailOps.remove.forEach(email => {
    if (!email.entryNode) return;
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingEmailNodes, email.entryNode);
    if (existingNode) {
      const linkedEmailStatements = (0, _rdfMutationHelpers.collectLinkedNodeStatements)(store, subject, _solidUi.ns.vcard('hasEmail'), doc);
      const matchingLinkStatement = linkedEmailStatements.linkStatements.find(statement => statement.object?.value === existingNode.value);
      if (matchingLinkStatement) {
        deletions.push(matchingLinkStatement);
      }
      deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
    }
  });
  emailOps.update.forEach(email => {
    if (!email.entryNode) return;
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingEmailNodes, email.entryNode);
    if (!existingNode) {
      insertions.push(...buildEmailStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), email));
      return;
    }
    deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
    insertions.push(...buildEmailStatements(subject, doc, existingNode, email));
  });
  emailOps.create.forEach(email => {
    insertions.push(...buildEmailStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), email));
  });
  const shouldSerializeDocument = await (0, _rdfMutationHelpers.shouldForceDocumentPutForStatements)(store, doc, insertions, undefined, {
    documentTextCache
  });
  await (0, _rdfMutationHelpers.runUpdateTransport)(store, doc, deletions, insertions, {
    unsupportedMessage: _texts.updaterUnsupportedStoreErrorMessageText,
    failureMessage: 'Failed to save contact info updates',
    documentTextCache,
    useDavFallback: false,
    usePutFallback: shouldSerializeDocument,
    forcePut: shouldSerializeDocument
  });
}
async function mutateAddressEntries(store, subject, addressOps, documentTextCache) {
  const doc = subject.doc();
  const existingAddressNodes = store.each(subject, _solidUi.ns.vcard('hasAddress'), null, doc);
  const deletions = [];
  const insertions = [];
  addressOps.remove.forEach(address => {
    if (!address.entryNode) return;
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingAddressNodes, address.entryNode);
    if (existingNode) {
      const linkedAddressStatements = (0, _rdfMutationHelpers.collectLinkedNodeStatements)(store, subject, _solidUi.ns.vcard('hasAddress'), doc);
      const matchingLinkStatement = linkedAddressStatements.linkStatements.find(statement => statement.object?.value === existingNode.value);
      if (matchingLinkStatement) {
        deletions.push(matchingLinkStatement);
      }
      deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
    }
  });
  addressOps.update.forEach(address => {
    if (!address.entryNode) return;
    const existingNode = (0, _rdfMutationHelpers.findExistingNode)(existingAddressNodes, address.entryNode);
    if (!existingNode) {
      insertions.push(...buildAddressStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), address));
      return;
    }
    deletions.push(...(0, _rdfMutationHelpers.collectNodeStatements)(store, existingNode, doc));
    insertions.push(...buildAddressStatements(subject, doc, existingNode, address));
  });
  addressOps.create.forEach(address => {
    insertions.push(...buildAddressStatements(subject, doc, (0, _idNodeFactory.createIdNode)(doc), address));
  });
  const shouldSerializeDocument = await (0, _rdfMutationHelpers.shouldForceDocumentPutForStatements)(store, doc, insertions, undefined, {
    documentTextCache
  });
  await (0, _rdfMutationHelpers.runUpdateTransport)(store, doc, deletions, insertions, {
    unsupportedMessage: _texts.updaterUnsupportedStoreErrorMessageText,
    failureMessage: 'Failed to save contact info updates',
    documentTextCache,
    useDavFallback: false,
    usePutFallback: shouldSerializeDocument,
    forcePut: shouldSerializeDocument
  });
}
async function processContactInfoMutations(store, subject, mutationPlan) {
  try {
    const documentTextCache = new Map();
    await mutatePhoneEntries(store, subject, mutationPlan.phoneOps, documentTextCache);
    await mutateEmailEntries(store, subject, mutationPlan.emailOps, documentTextCache);
    await mutateAddressEntries(store, subject, mutationPlan.addressOps, documentTextCache);
  } catch (error) {
    const rootError = error instanceof Error ? error : new Error(String(error));
    (0, _debug.error)(_texts.contactInfoMutationSaveFailedDebugText, rootError);
    throw new Error(_texts.saveContactUpdatesFailedMessageText, {
      cause: rootError
    });
  }
}